import { useState } from "react"
import {
  ArrowUp,
  Check,
  ChevronDown,
  Code2,
  MessageSquare,
  Paperclip,
  Play,
  FileSpreadsheet,
} from "lucide-react"

import { CODE_PUBLIC } from "@anysola/contracts/web/launch"
import { StatusLabel } from "@anysola/ui/status-label"
import { segmentClass, segmentGroupClass } from "@/components/layout/nav-link"
import { Tile } from "@/components/home/tile"
import { BigNumber } from "@anysola/ui/big-number"
import { RowIcon } from "@anysola/ui/row-group"
import { ACCOUNT_URL, API_BASE, CHAT_URL, CLOUD_URL } from "@/lib/app-links"
import { cn } from "@anysola/ui/utils"

/*
 * The home page's first screen under the heading: pieces of the real apps,
 * with sample values: Chat and Cloud 7/5, Code across the full width (a
 * terminal reads best wide; only once `CODE_PUBLIC`), then Account and
 * Support 5/7. Each piece is the app's own layout in miniature (Chat's thread
 * and composer, Cloud's request, a Code session in the terminal, Account's
 * balances, a support ticket), not a drawing of it. Sample interfaces are
 * `aria-hidden`; the Cloud request is real code and stays readable.
 */

const tileHeight = "h-[23rem] sm:h-[25rem]"

function Mosaic() {
  return (
    <div className="grid gap-3 lg:grid-cols-12">
      <Tile
        id="tile-chat"
        name="Anysola Chat"
        line="Ask, attach a file, get a chart"
        href={CHAT_URL}
        className={cn(tileHeight, "lg:col-span-7")}
      >
        <ChatSample />
      </Tile>
      <Tile
        id="tile-cloud"
        name="Anysola Cloud"
        line="One key for the API"
        href={CLOUD_URL}
        className={cn(tileHeight, "lg:col-span-5")}
      >
        <CloudRequest />
      </Tile>
      {CODE_PUBLIC && (
        <Tile
          id="tile-code"
          name="Anysola Code"
          line="A coding agent in your terminal"
          href="/code"
          // Full width, the session needs less height than the others.
          className={cn(tileHeight, "lg:col-span-12 lg:h-[21rem]")}
        >
          <CodeSession />
        </Tile>
      )}
      <Tile
        id="tile-account"
        name="Account"
        line="One balance for every app"
        href={`${ACCOUNT_URL}/billing`}
        className={cn(tileHeight, "lg:col-span-5")}
      >
        <BalanceSample />
      </Tile>
      <Tile
        id="tile-support"
        name="Support"
        line="A first answer in minutes"
        href="/help"
        className={cn(tileHeight, "lg:col-span-7")}
      >
        <TicketSample />
      </Tile>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

/** Orders per month in the sample answer; September is the answer. */
const MONTHS = [
  ["Jan", 612],
  ["Feb", 540],
  ["Mar", 734],
  ["Apr", 801],
  ["May", 768],
  ["Jun", 905],
  ["Jul", 1012],
  ["Aug", 1146],
  ["Sep", 1284],
] as const

function ChatSample() {
  const max = Math.max(...MONTHS.map(([, v]) => v))
  return (
    <div aria-hidden="true" className="flex h-full flex-col">
      <div className="flex items-center justify-between px-4 py-3 text-[13px] sm:px-5">
        <span className="truncate text-foreground">Orders by month</span>
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          Sonnet
          <ChevronDown className="size-3.5" />
        </span>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden px-4 sm:px-5">
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-2xl bg-surface-secondary px-3.5 py-2.5 text-[13px] text-foreground">
            <span className="mb-2 flex items-center gap-2 rounded-lg bg-default px-2.5 py-1.5 text-xs text-muted-foreground">
              <FileSpreadsheet className="size-3.5 shrink-0" />
              orders-2026.csv
            </span>
            Which month sold the most?
          </div>
        </div>
        <div className="max-w-[92%] text-[13px] leading-relaxed text-foreground">
          <p>
            September, with 1,284 orders. Every month since May grew on the one
            before.
          </p>
          <div className="mt-3 rounded-xl bg-card p-3">
            <div className="flex h-14 items-end gap-1.5 sm:h-24">
              {MONTHS.map(([month, value], i) => (
                <div key={month} className="flex h-full flex-1 items-end">
                  <span
                    className={cn(
                      "w-full rounded-[3px]",
                      i === MONTHS.length - 1 ? "bg-accent" : "bg-default"
                    )}
                    style={{ height: `${Math.round((value / max) * 100)}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="mt-1.5 flex gap-1.5 text-[10px] text-muted-foreground">
              {MONTHS.map(([month]) => (
                <span key={month} className="flex-1 text-center">
                  {month}
                </span>
              ))}
            </div>
          </div>
          <span className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Play className="size-3" />
            Ran 1 step
          </span>
        </div>
      </div>
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-2 rounded-2xl bg-card py-2 pr-2 pl-3.5">
          <Paperclip className="size-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate text-[13px] text-muted-foreground">
            Ask about this file
          </span>
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <ArrowUp className="size-4" />
          </span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Cloud
// ---------------------------------------------------------------------------

const SNIPPETS = {
  curl: `curl ${API_BASE}/account \\
  -H "Authorization: Bearer $ANYSOLA_API_KEY"`,
  Python: `import os, requests

r = requests.get(
    "${API_BASE}/account",
    headers={"Authorization":
        f"Bearer {os.environ['ANYSOLA_API_KEY']}"},
)
print(r.json())`,
  Node: `const res = await fetch("${API_BASE}/account", {
  headers: {
    Authorization: \`Bearer \${process.env.ANYSOLA_API_KEY}\`,
  },
})
console.log(await res.json())`,
} as const

type Lang = keyof typeof SNIPPETS

/** Cloud's docs example for `GET /v1/account`: the request in three
 *  languages and the documented response. */
const RESPONSE = `{
  "plan": "pro",
  "credits": {
    "available": 2280,
    "allowance": 2000,
    "renewsAt": "2026-11-01T00:00:00Z"
  }
}`

function CloudRequest() {
  const [lang, setLang] = useState<Lang>("curl")
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
        <div
          role="group"
          aria-label="Language"
          className={cn(segmentGroupClass, "bg-card")}
        >
          {(Object.keys(SNIPPETS) as Lang[]).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={lang === value}
              onClick={() => setLang(value)}
              className={cn(segmentClass(lang === value), "px-3 py-1 text-xs")}
            >
              {value}
            </button>
          ))}
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">
          GET /v1/account
        </span>
      </div>
      <pre className="min-h-0 overflow-hidden px-4 pt-1 pb-4 font-mono text-[12px] leading-[1.65] text-foreground/90 sm:px-5">
        <code>{SNIPPETS[lang]}</code>
      </pre>
      <div className="mt-auto min-h-0 overflow-hidden bg-card/60 px-4 py-3 sm:px-5">
        <p className="mb-1.5 flex items-center gap-3 text-[11px] text-muted-foreground">
          <StatusLabel tone="success" className="text-[11px]">
            200
          </StatusLabel>
          <span className="tabular-nums">Free · 84 ms</span>
        </p>
        <pre
          aria-label="Response"
          className="font-mono text-[12px] leading-[1.6] text-muted-foreground"
        >
          {RESPONSE}
        </pre>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Code
// ---------------------------------------------------------------------------

/** An Anysola Code session: a prompt, the tool calls it
 *  ran, the answer, and the input line with the agent and model. */
const TOOL_CALLS = [
  ["Read", "src/cart.ts", null],
  ["Edit", "src/cart.test.ts", null],
  ["Bash", "pnpm test cart", "14 passed"],
] as const

function CodeSession() {
  return (
    <div
      aria-hidden="true"
      className="flex h-full flex-col font-mono text-[12px] leading-[1.65]"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <span className="truncate text-foreground">~/shop</span>
        <span className="shrink-0 text-muted-foreground">anysola</span>
      </div>
      <div className="min-h-0 w-full max-w-2xl flex-1 space-y-3 overflow-hidden px-4 sm:px-5">
        <p className="rounded-lg bg-card px-3 py-2 text-foreground">
          Add a test for discount codes
        </p>
        <ul className="space-y-1">
          {TOOL_CALLS.map(([tool, target, result]) => (
            <li key={tool} className="flex gap-2 text-muted-foreground">
              <span className="w-9 shrink-0 text-foreground">{tool}</span>
              <span className="truncate">{target}</span>
              {result && (
                <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 sm:ml-4">
                  <Check className="size-3.5 text-success" />
                  {result}
                </span>
              )}
            </li>
          ))}
        </ul>
        <p className="font-sans text-[13px] leading-relaxed text-foreground">
          Added a test for a 10% code on a $42 cart. The total comes to $37.80
          and the suite passes.
        </p>
      </div>
      <div className="p-3 sm:p-4">
        <div className="rounded-2xl bg-card px-3.5 py-2">
          <span className="block truncate text-muted-foreground">
            Ask anything
          </span>
          <span className="mt-1 flex items-center gap-2 text-[11px]">
            <span className="text-foreground">Build</span>
            <span className="truncate text-muted-foreground">
              Sonnet 5.5 · Anysola
            </span>
          </span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

function BalanceSample() {
  return (
    <div aria-hidden="true" className="flex h-full flex-col gap-0.5">
      <div className="grid grid-cols-2 gap-0.5">
        <div className="bg-card/50 p-4 sm:p-5">
          <p className="text-[13px] text-muted-foreground">Credits</p>
          <BigNumber value="620" size="md" className="mt-3" />
          <span className="mt-4 block h-1.5 overflow-hidden rounded-full bg-default">
            <span className="block h-full w-[62%] rounded-full bg-accent" />
          </span>
          <p className="mt-2 text-xs text-muted-foreground tabular-nums">
            620 of 1,000 left
          </p>
        </div>
        <div className="bg-card/50 p-4 sm:p-5">
          <p className="text-[13px] text-muted-foreground">API credits</p>
          <BigNumber value="2,280" size="md" className="mt-3" />
          <span className="mt-4 block h-1.5 overflow-hidden rounded-full bg-default">
            <span className="block h-full w-full rounded-full bg-accent" />
          </span>
          <p className="mt-2 text-xs text-muted-foreground tabular-nums">
            280 bonus
          </p>
        </div>
      </div>
      {[
        {
          icon: <MessageSquare />,
          name: "Anysola Chat",
          when: "Used 2h ago",
          amount: "−38",
        },
        {
          icon: <Code2 />,
          name: "Anysola Cloud",
          when: "Used 5h ago",
          amount: "−12",
        },
      ].map((app) => (
        <div
          key={app.name}
          className="flex items-center gap-3 bg-card/50 px-4 py-3 sm:px-5"
        >
          <RowIcon>{app.icon}</RowIcon>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] text-foreground">
              {app.name}
            </span>
            <span className="block text-xs text-muted-foreground">
              {app.when}
            </span>
          </span>
          <span className="text-[13px] text-muted-foreground tabular-nums">
            {app.amount}
          </span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Support
// ---------------------------------------------------------------------------

function TicketSample() {
  return (
    <div aria-hidden="true" className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
        <span className="min-w-0">
          <span className="block truncate text-[13px] text-foreground">
            Credits didn’t refresh
          </span>
          <span className="block font-mono text-[11px] text-muted-foreground">
            T-1042
          </span>
        </span>
        <StatusLabel tone="warning">In progress</StatusLabel>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden px-4 sm:px-5">
        <div className="flex justify-end">
          <div className="max-w-[78%] rounded-2xl bg-surface-secondary px-3.5 py-2.5">
            <p className="text-xs text-muted-foreground">Alex · 2h ago</p>
            <p className="mt-1 text-[13px] leading-relaxed text-foreground">
              My plan renewed on the 1st but the balance didn’t change.
            </p>
          </div>
        </div>
        <div className="max-w-[78%] rounded-2xl bg-card px-3.5 py-2.5">
          <p className="text-xs text-muted-foreground">
            Anysola AI assistant · 2h ago
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-foreground">
            Credits refresh at the start of your billing cycle, not the calendar
            month. Yours renews on the 3rd.
          </p>
        </div>
      </div>
      <div className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2 rounded-2xl bg-card py-2 pr-2 pl-3.5">
          <span className="truncate text-[13px] text-muted-foreground">
            Reply
          </span>
          <span className="rounded-full bg-default px-3 py-1 text-xs text-foreground">
            Send
          </span>
        </div>
      </div>
    </div>
  )
}

export { Mosaic }
